import React, { useState, useCallback, useEffect } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import { VerticalLine } from "../../vertical-line/VerticalLine";
import _ from "lodash";
import { useUpdateArtifactDetailsByIdMutation } from "../../../endpoints/updateArtifactDetailsById";
import { useAppSelector } from "../../../app/store/hooks";
import { useParams } from "react-router-dom";
import Select, { SelectOption } from "@amzn/meridian/select";
import Loader from "@amzn/meridian/loader";
import {
    fetchPreSignedUrl,
    getImageTypeLabel,
    handleDownloadAllImages,
    updateArtifactDetails,
} from "./utils";
import { ImageGalleryModalProps, ImageItem } from "./definitions";
import { Space } from "../../space/Space";

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
    data,
    isOpen,
    onClose,
    startIndex = 0,
    setFileUploadModal,
    imageTypes,
}) => {
    const [images, setImages] = useState<ImageItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const [galleryImages, setGalleryImages] = useState<any[]>([]);
    const [updateArtifact] = useUpdateArtifactDetailsByIdMutation();
    const { alias } = useAppSelector(state => state.auth.user);
    const { propertyId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [editingType, setEditingType] = useState("");
    const [loading, setLoading] = useState(false);

    const handleImageChange = useCallback((index: number) => {
        setCurrentIndex(index);
        setIsEditing(false);
    }, []);

    useEffect(() => {
        const dataImages = _.get(data, "propertyDetails.propertyImages", []);
        setImages(dataImages);
        setCurrentIndex(0);
    }, [data]);

    useEffect(() => {
        const fetchImages = async () => {
            setLoading(true);
            const galleryImagesWithUrls = await Promise.all(
                images.map(async image => {
                    const presignedUrl = await fetchPreSignedUrl(image.canopyS3Key);

                    return presignedUrl
                        ? {
                              original: presignedUrl,
                              thumbnail: presignedUrl,
                              description: image.fileName,
                              originalTitle: image.type,
                              thumbnailTitle: image.type,
                              ...image,
                          }
                        : null;
                })
            );
            setGalleryImages(galleryImagesWithUrls.filter(Boolean));
            setLoading(false);
        };

        if (images.length > 0) {
            fetchImages();
        }
    }, [images]);

    const handleModalClose = () => {
        setFileUploadModal(true);
        onClose();
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setEditingType(galleryImages[currentIndex]?.type || "");
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingType("");
    };

    const handleSaveEdit = async () => {
        const updatedImages = images.map((img, index) =>
            index === currentIndex ? { ...img, type: editingType } : img
        );

        try {
            await updateArtifactDetails(updateArtifact, data, propertyId, alias, updatedImages);
            setImages(updatedImages);
            setGalleryImages(prevGalleryImages =>
                prevGalleryImages.map((img, index) =>
                    index === currentIndex ? { ...img, type: editingType } : img
                )
            );
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating image type:", error);
        }
    };

    const handleDelete = async () => {
        if (galleryImages.length === 0) return;

        const imageToDelete = galleryImages[currentIndex];
        const updatedImages = images.filter(img => img.canopyS3Key !== imageToDelete.canopyS3Key);

        try {
            await updateArtifactDetails(updateArtifact, data, propertyId, alias, updatedImages);
            setImages(updatedImages);
            setGalleryImages(prevGalleryImages =>
                prevGalleryImages.filter(img => img.canopyS3Key !== imageToDelete.canopyS3Key)
            );
            if (currentIndex >= updatedImages.length) {
                setCurrentIndex(Math.max(0, updatedImages.length - 1));
            }
        } catch (error) {
            console.error("Error deleting image:", error);
        }
    };

    const renderCustomControls = () => {
        if (galleryImages.length === 0) return null;

        return (
            <Row widths={["fit", "fit", "fill"]} spacingInset={"300"}>
                <Text type="h300">{galleryImages[currentIndex]?.fileName || ""}</Text>
                <VerticalLine height={20} />
                {isEditing ? (
                    <>
                        <Select
                            value={editingType}
                            onChange={setEditingType}
                            placeholder="Select image type..."
                            width={300}
                        >
                            {imageTypes.map(type => (
                                <SelectOption
                                    key={type.value}
                                    value={type.value}
                                    label={type.label}
                                />
                            ))}
                        </Select>
                        <Button onClick={handleSaveEdit}>Save</Button>
                        <Button onClick={handleCancelEdit} type={"secondary"}>
                            Cancel
                        </Button>
                    </>
                ) : (
                    <>
                        <Text>{getImageTypeLabel(galleryImages[currentIndex]?.type) || ""}</Text>
                        <Button onClick={handleEditClick} type={"tertiary"}>
                            Edit tag
                        </Button>
                    </>
                )}
                <Button onClick={handleDelete} type={"tertiary"}>
                    Delete
                </Button>
            </Row>
        );
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            closeLabel="Close gallery"
            scrollContainer="viewport"
            width="1000px"
            title="View image"
        >
            {loading ? (
                <Loader size={"large"} />
            ) : galleryImages.length > 0 ? (
                <ImageGallery
                    items={galleryImages}
                    startIndex={currentIndex}
                    showPlayButton={false}
                    showFullscreenButton={true}
                    showNav={true}
                    showThumbnails={true}
                    thumbnailPosition="left"
                    useBrowserFullscreen={false}
                    onSlide={handleImageChange}
                    renderCustomControls={renderCustomControls}
                />
            ) : (
                <Column spacing="200" alignmentHorizontal="center" alignmentVertical="center">
                    <Text id="modal-description">{"No images available"}</Text>
                </Column>
            )}

            <ModalFooter>
                <Row
                    alignmentHorizontal="end"
                    widths="fit"
                    spacingInset={"400"}
                    spacing={"400"}
                    width={"100%"}
                >
                    <Space size={"l"} />
                    <Button type="secondary" onClick={handleModalClose}>
                        Upload image
                    </Button>
                    <Button
                        type="secondary"
                        onClick={() => {
                            handleDownloadAllImages(setLoading, galleryImages);
                        }}
                        disabled={galleryImages.length === 0 || loading}
                    >
                        Download all images
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
